import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import RecycleOfferCard from './RecycleOfferCard';

/**
 * Cards per slide must align with sustainable-future.css (1367px = 3 columns).
 * Use window width, not the track viewport: on desktop the track is only ~55% of
 * the window, so it would always be ≤1366px and never show 3 cards.
 */
function getCardsPerSlide(trackViewportWidth) {
    if (typeof window !== 'undefined' && window.innerWidth > 0) {
        const w = window.innerWidth;
        if (w < 768) return 1;
        if (w <= 1366) return 2;
        return 3;
    }
    if (!trackViewportWidth || trackViewportWidth <= 0) return 2;
    if (trackViewportWidth < 768) return 1;
    if (trackViewportWidth <= 1366) return 2;
    return 3;
}

function getSliderGap(viewportWidth) {
    /* Mobile track uses gap: 0 in sustainable-future.css — match or width math breaks */
    return viewportWidth < 768 ? 0 : 10;
}

const RecycleOffersSlider = ({ offers, loading }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderTrackRef = useRef(null);
    const prevBtnRef = useRef(null);
    const nextBtnRef = useRef(null);

    /** Keep slide index valid when cards-per-slide changes (e.g. mobile → desktop). */
    useEffect(() => {
        if (offers.length === 0) return undefined;

        const clampSlideToViewport = () => {
            const viewport = sliderTrackRef.current?.parentElement;
            if (!viewport) return;
            const vw = viewport.offsetWidth;
            if (vw <= 0) return;
            const cardsPerSlide = getCardsPerSlide(vw);
            const totalSlides = Math.max(1, Math.ceil(offers.length / cardsPerSlide));
            setCurrentSlide((prev) => Math.min(Math.max(0, prev), totalSlides - 1));
        };

        clampSlideToViewport();
        window.addEventListener('resize', clampSlideToViewport);
        return () => window.removeEventListener('resize', clampSlideToViewport);
    }, [offers.length]);

    const updateSliderLayout = useCallback(() => {
        const track = sliderTrackRef.current;
        if (!track || offers.length === 0) return;

        const viewport = track.parentElement;
        if (!viewport) return;

        const viewportWidth = viewport.offsetWidth;
        if (viewportWidth <= 0) return;

        const gap = getSliderGap(viewportWidth);
        const cardsPerSlide = getCardsPerSlide(viewportWidth);

        const totalCards = offers.length;
        const totalSlides = Math.max(1, Math.ceil(totalCards / cardsPerSlide));
        const safeSlide = Math.min(Math.max(0, currentSlide), totalSlides - 1);

        const cardWidth =
            cardsPerSlide <= 1
                ? viewportWidth
                : (viewportWidth - gap * (cardsPerSlide - 1)) / cardsPerSlide;
        const cardStep = cardsPerSlide <= 1 ? viewportWidth : cardWidth + gap;

        const firstVisibleIndex = safeSlide * cardsPerSlide;
        const translateX = -firstVisibleIndex * cardStep;

        track.style.width = `${totalCards * cardWidth + Math.max(0, totalCards - 1) * gap}px`;
        track.style.setProperty('--slider-card-width', `${cardWidth}px`);
        track.style.setProperty('--slider-gap', `${gap}px`);
        track.style.transform = `translateX(${translateX}px)`;

        if (prevBtnRef.current) {
            prevBtnRef.current.disabled = safeSlide === 0;
        }
        if (nextBtnRef.current) {
            nextBtnRef.current.disabled = safeSlide >= totalSlides - 1;
        }
    }, [offers, currentSlide]);

    useLayoutEffect(() => {
        if (offers.length === 0) return undefined;
        updateSliderLayout();

        const track = sliderTrackRef.current;
        const viewport = track?.parentElement;
        if (viewport && viewport.offsetWidth > 0) {
            const cps = getCardsPerSlide(viewport.offsetWidth);
            const totalSlides = Math.max(1, Math.ceil(offers.length / cps));
            const safe = Math.min(Math.max(0, currentSlide), totalSlides - 1);
            if (safe !== currentSlide) setCurrentSlide(safe);
        }

        const onResize = () => updateSliderLayout();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [offers, offers.length, currentSlide, updateSliderLayout]);

    const handlePrev = () => {
        setCurrentSlide((prev) => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        const viewport = sliderTrackRef.current?.parentElement;
        if (!viewport) return;

        const viewportWidth = viewport.offsetWidth;
        const cardsPerSlide = getCardsPerSlide(viewportWidth);
        const totalCards = offers.length;
        const totalSlides = Math.max(1, Math.ceil(totalCards / cardsPerSlide));
        setCurrentSlide((prev) => {
            const next = prev + 1;
            if (next >= totalSlides) return prev;
            return next;
        });
    };

    if (loading) {
        return (
            <div className="recycled-lots-section">
                <div className="lots-slider-container" data-slider-id="recycled-lots">
                    <div className="lots-slider-viewport">
                        <div className="lots-slider-track" id="recycled-lots-track">
                            <div className="lots-loading-state">
                                <i className="fas fa-spinner fa-spin"></i>
                                <p>Loading recycled polymer lots...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div className="recycled-lots-section recycled-lots-section--empty">
                <div className="lots-slider-container lots-slider-container--empty" data-slider-id="recycled-lots">
                    <div className="lots-slider-viewport lots-slider-viewport--empty">
                        <div className="lots-slider-track lots-slider-track--empty" id="recycled-lots-track">
                            <div className="lots-empty-state">
                                <p>No offers at the moment.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="recycled-lots-section">
            <div className="lots-slider-container" data-slider-id="recycled-lots">
                <div className="lots-slider-viewport">
                    <div
                        className="lots-slider-track"
                        id="recycled-lots-track"
                        ref={sliderTrackRef}
                    >
                        {offers.map((offer, index) => (
                            <RecycleOfferCard key={offer.id || index} offer={offer} />
                        ))}
                    </div>
                </div>
            </div>

            <div className="lots-slider-controls-bottom">
                <button
                    type="button"
                    className="slider-nav prev"
                    data-slider-control="recycled-lots"
                    data-direction="prev"
                    aria-label="Previous offers"
                    onClick={handlePrev}
                    ref={prevBtnRef}
                >
                    <i className="fas fa-chevron-left"></i>
                </button>
                <button
                    type="button"
                    className="slider-nav next"
                    data-slider-control="recycled-lots"
                    data-direction="next"
                    aria-label="Next offers"
                    onClick={handleNext}
                    ref={nextBtnRef}
                >
                    <i className="fas fa-chevron-right"></i>
                </button>
            </div>
        </div>
    );
};

export default RecycleOffersSlider;
